import { useEffect, useState, useContext } from "react"
import { AlertsContext } from "../../../../utils/providers/alerts"
import { db } from "../../../../utils/firebase"
import { NavLink } from "react-router-dom"

// UI imports
import Loading from "../../../../components/app/loading/loading"
import Button from "../../../../components/ui/button/button"
import Input from "../../../../components/ui/inputs/input"
import Table from "../../../../components/structure/table/table"
import Badge from "../../../../components/ui/badge/badge"

// Returns the HTML markup for the golf day tabs
export default function GolfDay({ guests, flag, eventId }) {
	const [loading, setLoading] = useState(true)
	const [players, setPlayers] = useState([])
	const [slopeRating, setSlopeRating] = useState(null)
	const [handicapPercentage, setHandicapPercentage] = useState(null)
	const [editingHandicaps, setEditingHandicaps] = useState({})
	const [editingScores, setEditingScores] = useState({})
	const [savingStates, setSavingStates] = useState({})
	const [savingParameters, setSavingParameters] = useState(false)

	// Get the alerts context
	const { pushAlert } = useContext(AlertsContext)

	// Calculate playing handicap
	const calculatePlayingHandicap = (handicap) => {
		if (!handicap || !slopeRating || !handicapPercentage) return null
		return Math.round(((handicap * slopeRating) / 113) * (handicapPercentage / 100))
	}

	// Save a player's handicap
	const savePlayerHandicap = async (guestID, handicap) => {
		try {
			// Find the player in the players array
			const player = players.find((player) => player.guestID === guestID)

			// Set the saving state to true
			setSavingStates((prev) => ({ ...prev, [guestID]: true }))

			// Save the handicap to the database
			await db.doc(`bookings/${player.bookingID}/group_names/${guestID}`).set(
				{
					handicap: parseInt(handicap),
					golf_day_scores: {
						[flag]: {
							score: 0,
						},
					},
				},
				{ merge: true }
			)

			pushAlert({
				type: "SUCCESS",
				title: "Handicap saved successfully",
			})
		} catch (err) {
			console.error("Error saving handicap:", err)
			pushAlert({
				type: "ERROR",
				title: "Failed to save handicap",
			})
		} finally {
			setSavingStates((prev) => ({ ...prev, [guestID]: false }))
		}
	}

	// Save a player's score
	const savePlayerScore = async (guestID, score) => {
		try {
			// Find the player in the players array
			const player = players.find((player) => player.guestID === guestID)

			// Set the saving state to true
			setSavingStates((prev) => ({ ...prev, [guestID]: true }))

			// Save the score to the database
			await db.doc(`bookings/${player.bookingID}/group_names/${guestID}`).set(
				{
					golf_day_scores: {
						[flag]: {
							score: parseInt(score),
						},
					},
				},
				{ merge: true }
			)

			pushAlert({
				type: "SUCCESS",
				title: "Score saved successfully",
			})
		} catch (err) {
			console.error("Error saving score:", err)
			pushAlert({
				type: "ERROR",
				title: "Failed to save score",
			})
		} finally {
			setSavingStates((prev) => ({ ...prev, [guestID]: false }))
		}
	}

	// Save the parameters to the database
	const saveParameters = async () => {
		try {
			setSavingParameters(true)
			await db.doc(`events/${eventId || "masters_2025"}/golf_days/${flag}`).set(
				{
					slope_rating: parseInt(slopeRating),
					handicap_percentage: parseInt(handicapPercentage),
				},
				{ merge: true }
			)

			pushAlert({
				type: "SUCCESS",
				title: "Parameters saved successfully",
			})
		} catch (err) {
			console.error("Error saving parameters:", err)
		} finally {
			setSavingParameters(false)
		}
	}

	// On component load
	useEffect(() => {
		let playersUnsubscribe = null
		try {
			const eid = eventId || "masters_2025"
			// Fetch the settings pre-sepcified in the database for this golf day
			db.doc(`events/${eid}/golf_days/${flag}`)
				.get()
				.then((dayDoc) => {
					if (dayDoc.exists) {
						// Pull the data from the document
						const { slope_rating, handicap_percentage } = dayDoc.data()

						// Set the parameters into the state
						setSlopeRating(slope_rating)
						setHandicapPercentage(handicap_percentage)
					}

					// Set the loading to false
					setLoading(false)
				})

			// And also setup a listener for the players
			playersUnsubscribe = db
				.collectionGroup("group_names")
				.where(`golf_days.${flag}`, "==", true)
				.onSnapshot((snapshot) => {
					// Filter the guests to only include those that have a booking
					const filteredGuests = guests.filter((guest) => guest.booking_reference !== "-")

					// Create an array to store the players
					let localPlayers = []

					// Loop through the snapshot and add the players to the local players array
					snapshot.docs.map((doc) => {
						// Find the guest in the guests array
						const guest = filteredGuests.find((guest) => guest.guestID === doc.id)

						// Return the guest with the golf day data
						localPlayers.push({
							...guest,
							handicap: doc.data().handicap,
						})
					})

					// Remove any which don't have a first/last name
					localPlayers = localPlayers.filter((player) => player.first_name && player.last_name)

					// And then order them by last name, ensuring whitespaces are trimmed
					localPlayers = localPlayers.sort((a, b) => {
						const lastNameA = (a.last_name || "").trim()
						const lastNameB = (b.last_name || "").trim()
						return lastNameA.localeCompare(lastNameB)
					})

					// Set the players to the local players array
					setPlayers(localPlayers)

					// Pre-fill the editing handicaps with saved values
					const initialHandicaps = {}
					localPlayers.forEach((player) => {
						if (player.handicap) {
							initialHandicaps[player.guestID] = player.handicap
						}
					})
					setEditingHandicaps(initialHandicaps)
				})
		} catch (err) {
			console.error("Error fetching golf day settings:", err)
		} finally {
			setLoading(false)
		}
		return () => {
			if (playersUnsubscribe) playersUnsubscribe()
		}
	}, [eventId, flag, guests])

	// If we're still loading the data in, show a loading indicator
	if (loading) return <Loading />

	return (
		<>
			<div style={{ display: "flex", gap: "12px", marginBottom: "20px" }}>
				<div style={{ width: "170px" }}>
					<Input
						showLabel={true}
						value={slopeRating}
						onChange={(value) => setSlopeRating(value)}
						placeholder="Slope rating"
						type="number"
					/>
				</div>
				<div style={{ width: "170px" }}>
					<Input
						showLabel={true}
						value={handicapPercentage}
						onChange={(value) => setHandicapPercentage(value)}
						placeholder="Hcp. percentage"
						type="number"
					/>
				</div>
				<div>
					<Button
						label="Save"
						type="SECONDARY"
						loading={savingParameters}
						loadingText="Saving..."
						onClick={() => saveParameters()}
					/>
				</div>
			</div>

			<small style={{ display: "block", fontSize: "14px", color: "#585858", marginBottom: "12px" }}>Showing {players.length} players</small>

			<Table
				className="bookings-table"
				headings={["Booking", "Guest Name", "Handicap", "Playing Handicap", "Points", "Club Hire", ""]}>
				{players.length > 0 ? (
					players.map((player) => {
						const playingHandicap = calculatePlayingHandicap(editingHandicaps[player.guestID] || player.handicap)
						return (
							<tr key={player.guestID}>
								<td>
									<NavLink to={`/booking/${player.bookingID}`}>{player.booking_reference !== "-" ? player.booking_reference : "Unknown"}</NavLink>
								</td>
								<td>{`${player.first_name || ""} ${player.last_name || ""}`.trim() || "-"}</td>
								<td>
									<div style={{ display: "grid", gap: "4px", gridTemplateColumns: "122px 55px", alignItems: "center" }}>
										<Input
											value={editingHandicaps[player.guestID] ?? player.handicap ?? ""}
											onChange={(value) => setEditingHandicaps((prev) => ({ ...prev, [player.guestID]: value }))}
											placeholder="Handicap:"
											type="number"
										/>
										<Button
											label="Save"
											small={true}
											loading={savingStates[player.guestID]}
											onClick={() => savePlayerHandicap(player.guestID, editingHandicaps[player.guestID])}
										/>
									</div>
								</td>
								<td>{playingHandicap ?? "-"}</td>
								<td>
									<div style={{ display: "grid", gap: "4px", gridTemplateColumns: "122px 55px", alignItems: "center" }}>
										<Input
											value={editingScores[player.guestID] ?? player.golf_day_scores?.[flag]?.score ?? ""}
											onChange={(value) => setEditingScores((prev) => ({ ...prev, [player.guestID]: value }))}
											placeholder="Score:"
											type="number"
										/>
										<Button
											label="Save"
											small={true}
											loading={savingStates[player.guestID]}
											onClick={() => savePlayerScore(player.guestID, editingScores[player.guestID])}
										/>
									</div>
								</td>
								<td>
									{player.club_hire ? (
										<div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
											<div>
												<Badge
													label="Yes"
													type="POSITIVE"
												/>
											</div>
											{player.club_hire_hand.charAt(0).toUpperCase() + player.club_hire_hand.slice(1)}-handed
										</div>
									) : (
										"-"
									)}
								</td>
								<td></td>
							</tr>
						)
					})
				) : (
					<tr>
						<td colSpan="8">No players found for {flag}</td>
					</tr>
				)}
			</Table>
		</>
	)
}
